<template>
	<v-container class="account-settings">
		<!-- Profile Card -->
		<v-card class="profile-card mb-6" flat>
			<div class="profile-banner"></div>
			<div class="profile-content">
				<v-avatar size="72" color="white" class="profile-avatar elevation-3">
					<span class="primary--text" style="font-size: 2.8rem; font-weight: 600;">{{ user.initials }}</span>
				</v-avatar>
				<div class="profile-info">
					<h2 class="profile-name">{{ user.firstName }} {{ user.lastName }}</h2>
					<div class="profile-username">{{ user.username }}</div>
					<div class="profile-roles">
						<v-chip
							v-for="role in userRoles"
							:key="role"
							x-small
							outlined
							class="mr-1"
							:color="isAdminRole(role) ? 'error' : 'grey darken-1'"
						>{{ formatRole(role) }}</v-chip>
					</div>
				</div>
			</div>
		</v-card>

		<!-- Label Printer (Shipper users) -->
		<v-card v-if="isShipper" class="mb-6" flat>
			<v-card-title class="section-header">
				<v-icon left color="primary">mdi-label-outline</v-icon>
				<div>
					<div>Label Printer</div>
					<div class="section-subtitle">
						Select which hardware printer to use for shipping labels.
					</div>
				</div>
			</v-card-title>

			<v-divider></v-divider>

			<v-card-text class="pa-5">
				<v-select
					v-model="selectedLabelPrinter"
					:items="uhbPrinters"
					item-text="name"
					item-value="id"
					label="Printer"
					outlined
					dense
					clearable
					placeholder="No printer selected"
					:disabled="!uhbPrinters.length"
				>
					<template v-slot:selection="{ item }">
						<div class="d-flex align-center">
							<v-icon small :color="item.isConnected ? 'success' : 'grey'" class="mr-2">
								{{ item.isConnected ? 'mdi-printer' : 'mdi-printer-off' }}
							</v-icon>
							{{ item.name }}
							<v-chip v-if="item.isConnected" x-small color="success" text-color="white" class="ml-2">Connected</v-chip>
						</div>
					</template>
					<template v-slot:item="{ item }">
						<div class="d-flex align-center">
							<v-icon small :color="item.isConnected ? 'success' : 'grey'" class="mr-2">
								{{ item.isConnected ? 'mdi-printer' : 'mdi-printer-off' }}
							</v-icon>
							{{ item.name }}
							<v-chip v-if="item.isConnected" x-small color="success" text-color="white" class="ml-2">Connected</v-chip>
						</div>
					</template>
				</v-select>

				<v-switch
					v-model="autoPrintLabels"
					:disabled="!selectedLabelPrinter"
					label="Automatically print shipping labels"
					color="primary"
					hide-details
					class="mt-1"
				></v-switch>

				<v-select
					v-model="selectedLabelLayout"
					:items="labelLayoutOptions"
					label="Default Label Size"
					outlined
					dense
					class="mt-5"
					hide-details
				></v-select>

				<v-alert v-if="!uhbPrinters.length" type="info" dense outlined class="mt-4">
					No printers detected. Make sure the Universal Hardware Bridge is running.
				</v-alert>
			</v-card-text>

			<v-divider></v-divider>

			<v-card-actions class="pa-4">
				<v-spacer></v-spacer>
				<v-btn
					color="primary"
					depressed
					:disabled="!labelPrinterChanged"
					@click="saveLabelPrinterSettings"
				>
					<v-icon left small>mdi-content-save</v-icon>
					Save
				</v-btn>
			</v-card-actions>
		</v-card>

		<!-- Printer Preferences -->
		<v-card flat>
			<v-card-title class="section-header">
				<v-icon left color="primary">mdi-printer-settings</v-icon>
				<div>
					<div>My Printer Preferences</div>
					<div class="section-subtitle">
						Override the system auto-print settings for your account.
						Documents will automatically print to your selected printer when enabled.
					</div>
				</div>
			</v-card-title>

			<v-divider></v-divider>

			<v-card-text class="pa-0">
				<v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

				<div v-if="error" class="pa-4">
					<v-alert type="error" dense>{{ error }}</v-alert>
				</div>

				<div v-if="!loading" class="config-list">
					<div
						v-for="config in printConfigurations"
						:key="config.documentType"
						class="config-row"
					>
						<div class="config-doc-type">
							<v-icon color="primary" class="mr-3">{{ config.documentTypeIcon }}</v-icon>
							<div>
								<div class="config-doc-name">{{ config.documentTypeName }}</div>
								<div class="config-doc-desc">{{ config.documentTypeDescription }}</div>
							</div>
						</div>

						<div class="config-controls">
							<div class="config-printer">
								<v-select
									v-model="config.printerId"
									:items="printerOptions"
									item-text="name"
									item-value="id"
									dense
									outlined
									clearable
									hide-details="auto"
									placeholder="Use system default"
									:disabled="!config.isEnabled"
									:error-messages="validationErrors[config.documentType]"
									@change="clearValidationError(config.documentType)"
								></v-select>
							</div>

							<div class="config-toggle">
								<v-switch
									v-model="config.isEnabled"
									color="success"
									hide-details
									dense
									class="mt-0"
								></v-switch>
							</div>

							<div class="config-status">
								<v-chip
									x-small
									:color="config.isUserOverride ? 'primary' : ''"
									:outlined="!config.isUserOverride"
									:text-color="config.isUserOverride ? 'white' : 'grey'"
								>{{ config.isUserOverride ? 'Custom' : 'Default' }}</v-chip>
							</div>
						</div>
					</div>

					<div v-if="printConfigurations.length === 0" class="text-center pa-6 grey--text">
						No print configurations available.
					</div>
				</div>
			</v-card-text>

			<v-divider></v-divider>

			<v-card-actions class="pa-4">
				<v-btn
					text
					small
					:disabled="loading || !hasUserOverrides"
					@click="resetToDefaults"
				>
					<v-icon left small>mdi-restore</v-icon>
					Reset to Defaults
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn
					color="primary"
					depressed
					:loading="saving"
					:disabled="loading || !hasChanges"
					@click="saveConfiguration"
				>
					<v-icon left small>mdi-content-save</v-icon>
					Save Preferences
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-container>
</template>

<script>
import { getPrinterConfiguration } from "@/services/PrinterConfigurationService";
import { getHardwareService } from "@/services/HardwareIntegrationService";
import { hasAnyRole, WellKnownRoles } from "@/models/User";

export default {
	name: "AccountSettings",
	data() {
		return {
			printers: [],
			printConfigurations: [],
			originalConfigurations: [],
			loading: false,
			saving: false,
			error: null,
			validationErrors: {},
			// Label printer (UHB)
			uhbPrinters: [],
			selectedLabelPrinter: null,
			autoPrintLabels: false,
			selectedLabelLayout: "4x6",
			savedLabelPrinter: null,
			savedAutoPrintLabels: false,
			savedLabelLayout: "4x6",
			labelLayoutOptions: [
				{ text: "4x6 - Standard Shipping", value: "4x6" },
				{ text: "4x8 - Large Label", value: "4x8" },
				{ text: "Letter - 8.5x11", value: "letter" },
			],
		};
	},
	computed: {
		user() {
			return this.$store.getters.userInfo || {};
		},
		userRoles() {
			return (this.user.roles || []).map((r) => r.name);
		},
		isShipper() {
			return this.user && hasAnyRole(this.user, [WellKnownRoles.SHIPPER]);
		},
		printerOptions() {
			return this.printers.map((printer) => ({
				id: printer.printerId,
				name: printer.name,
			}));
		},
		hasChanges() {
			if (this.printConfigurations.length !== this.originalConfigurations.length) {
				return true;
			}
			return this.printConfigurations.some((config, index) => {
				const original = this.originalConfigurations[index];
				if (!original) return true;
				return (
					config.printerId !== original.printerId ||
					config.isEnabled !== original.isEnabled
				);
			});
		},
		hasUserOverrides() {
			return this.printConfigurations.some((config) => config.isUserOverride);
		},
		labelPrinterChanged() {
			return (
				this.selectedLabelPrinter !== this.savedLabelPrinter ||
				this.autoPrintLabels !== this.savedAutoPrintLabels ||
				this.selectedLabelLayout !== this.savedLabelLayout
			);
		},
	},
	created() {
		this.loadData();
		if (this.isShipper) {
			this.loadLabelPrinterSettings();
		}
	},
	methods: {
		formatRole(role) {
			return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
		},

		isAdminRole(role) {
			return role === "admin" || role === "super_admin";
		},

		async loadData() {
			this.loading = true;
			this.error = null;
			try {
				const [printersRes, configRes] = await Promise.all([
					this.$http.get("/api/print/printers"),
					this.$http.get("/api/print/configuration/user"),
				]);

				const printersData = printersRes.data;
				this.printers = printersData.printers || printersData;

				this.printConfigurations = configRes.data.map((config) => ({
					...config,
					printerId: config.printerId || null,
					isEnabled: config.isEnabled || false,
				}));
				this.originalConfigurations = JSON.parse(
					JSON.stringify(this.printConfigurations)
				);
			} catch (err) {
				console.error("Error loading account settings:", err);
				this.error = "Failed to load printer settings.";
			} finally {
				this.loading = false;
			}
		},

		clearValidationError(documentType) {
			if (this.validationErrors[documentType]) {
				this.$delete(this.validationErrors, documentType);
			}
		},

		validate() {
			this.validationErrors = {};
			let valid = true;
			this.printConfigurations.forEach((config) => {
				if (config.isEnabled && !config.printerId) {
					this.$set(this.validationErrors, config.documentType, "Select a printer");
					valid = false;
				}
			});
			return valid;
		},

		async saveConfiguration() {
			if (!this.validate()) return;

			this.saving = true;
			try {
				const payload = this.printConfigurations.map((config) => ({
					documentType: config.documentType,
					printerId: config.printerId,
					isEnabled: config.isEnabled,
				}));

				await this.$http.post("/api/print/configuration/user", payload);
				this.originalConfigurations = JSON.parse(
					JSON.stringify(this.printConfigurations)
				);

				this.printConfigurations.forEach((c) => (c.isUserOverride = true));

				this.$toast?.success("Printer preferences saved");
			} catch (err) {
				console.error("Error saving printer preferences:", err);
				const msg =
					err.response?.data?.message ||
					err.response?.data ||
					"Failed to save printer preferences.";
				this.$toast?.error(msg);
			} finally {
				this.saving = false;
			}
		},

		async resetToDefaults() {
			try {
				await this.$http.delete("/api/print/configuration/user");
				this.$toast?.success("Printer preferences reset to system defaults");
				await this.loadData();
			} catch (err) {
				console.error("Error resetting preferences:", err);
				this.$toast?.error("Failed to reset preferences.");
			}
		},

		// Label Printer (UHB) methods
		async loadLabelPrinterSettings() {
			try {
				const hardwareService = getHardwareService();
				if (hardwareService.isConnected) {
					const printers = await hardwareService.discoverPrinters();
					this.uhbPrinters = printers.map((p) => ({
						id: p.id,
						name: p.name || p.id,
						isConnected: p.isConnected,
					}));
				}
			} catch (err) {
				console.warn("Could not discover UHB printers:", err);
			}

			const config = getPrinterConfiguration().getConfiguration();
			this.selectedLabelPrinter = config.labelPrinterId;
			this.autoPrintLabels = config.autoPrintLabels;
			this.selectedLabelLayout = config.defaultLabelLayout || "4x6";
			this.savedLabelPrinter = this.selectedLabelPrinter;
			this.savedAutoPrintLabels = this.autoPrintLabels;
			this.savedLabelLayout = this.selectedLabelLayout;
		},

		saveLabelPrinterSettings() {
			const configService = getPrinterConfiguration();
			configService.updateConfiguration({
				labelPrinterId: this.selectedLabelPrinter,
				autoPrintLabels: this.autoPrintLabels,
				defaultLabelLayout: this.selectedLabelLayout,
				pickTicketPrinterId: configService.getPickTicketPrinter() || this.selectedLabelPrinter,
			});
			this.savedLabelPrinter = this.selectedLabelPrinter;
			this.savedAutoPrintLabels = this.autoPrintLabels;
			this.savedLabelLayout = this.selectedLabelLayout;
			this.$toast?.success("Label printer settings saved");
		},
	},
};
</script>

<style lang="scss" scoped>
.account-settings {
	max-width: 800px;
}

// Profile Card
.profile-card {
	overflow: hidden;

	.profile-banner {
		height: 80px;
		background: linear-gradient(135deg, #c2262f 0%, #8b1a20 100%);
	}

	.profile-content {
		display: flex;
		align-items: flex-start;
		padding: 0 24px 20px;
		margin-top: -36px;
	}

	.profile-avatar {
		flex-shrink: 0;
		border: 3px solid white;
	}

	.profile-info {
		margin-left: 20px;
		padding-top: 40px;
	}

	.profile-name {
		font-size: 2rem;
		font-weight: 600;
		color: #333;
		line-height: 1.2;
	}

	.profile-username {
		font-size: 1.4rem;
		color: #888;
		margin-top: 2px;
	}

	.profile-roles {
		margin-top: 8px;
	}
}

// Section Header
.section-header {
	font-size: 1.6rem !important;
	padding: 16px 20px;
	align-items: flex-start;

	.v-icon {
		margin-top: 2px;
	}

	.section-subtitle {
		font-size: 1.3rem;
		font-weight: 400;
		color: #888;
		margin-top: 2px;
		line-height: 1.4;
	}
}

// Config List (replaces table)
.config-list {
	.config-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 20px;
		border-bottom: 1px solid #f0f0f0;
		transition: background-color 0.15s;

		&:last-child {
			border-bottom: none;
		}

		&:hover {
			background-color: #fafafa;
		}
	}

	.config-doc-type {
		display: flex;
		align-items: center;
		flex: 0 0 220px;
	}

	.config-doc-name {
		font-size: 1.4rem;
		font-weight: 500;
		color: #333;
	}

	.config-doc-desc {
		font-size: 1.2rem;
		color: #999;
	}

	.config-controls {
		display: flex;
		align-items: center;
		flex: 1;
		justify-content: flex-end;
		gap: 16px;
	}

	.config-printer {
		width: 260px;
	}

	.config-toggle {
		flex-shrink: 0;
	}

	.config-status {
		flex-shrink: 0;
		width: 70px;
		text-align: center;
	}
}
</style>
